import {
  Injectable,
  signal,
  computed,
  effect,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { Cart, CartItem, Sale, TransactionType } from '../models/sale.model';
import { PriceList } from '../models/multi-tenant/price-list.model';
import { Product } from '../models/product-v2.model';
import { PosSettingsService } from './pos-settings.service';
import { SoundService } from './sound.service';
import { EmployeeService } from './employee.service';
import { HeldOrderService } from './held-order.service';
import { applyRounding } from '../../shared/utils/rounding.utils';
import { saleItemToCartItem, soldUnitPrice } from '../../shared/utils/cart-item.utils';
import { resolvePrice } from '../../shared/utils/price-resolution.utils';
import {
  LinePricingContext,
  RepricingCounts,
  RepricingSummary,
  lineTotal,
  listById,
  priceIsSettled,
  priceLine,
  repriceLines,
} from '../../shared/utils/price-line-engine.utils';
import { CurrentStock, refreshCartItemStock } from '../../shared/utils/stock-revalidation.utils';

/**
 * What a repricing did to the open cart, for the POS to tell the cashier.
 *
 * Re-exported from the engine, which owns it now that Facturación reports the
 * very same tally with the very same sentence (`repricingMessage`).
 */
export type { RepricingSummary };

/**
 * The catalogue figures a lapsed quotation's lines need back before they can be
 * repriced (see {@link CartService.repriceLapsedQuote}).
 *
 * A stored `SaleItem` is a deliberately lossy snapshot: it keeps the price that
 * was *charged* and nothing of where that price started, so rehydrating a quote
 * leaves `basePrice` holding the charged figure. Handing that to the engine
 * would apply today's adjustment to an already adjusted number. This carries the
 * real ones back in, read from the products themselves.
 */
export interface CatalogSnapshot {
  /** The products behind the lines, by product id. */
  products: Map<string, Product>;
  /**
   * Catalogue prices of the variants on the lines, keyed `${productId}:${variantId}`.
   * A variant overrides its product's `basePrice`, and no `Product` document
   * carries them — they live in a subcollection, so they are read separately and
   * only for the lines that actually have a variant.
   */
  variantPrices: Map<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private posSettingsService = inject(PosSettingsService);
  private soundService = inject(SoundService);
  private employeeService = inject(EmployeeService);
  private heldOrderService = inject(HeldOrderService);
  private injector = inject(Injector);
  // Current cashier ID for cart isolation
  private currentCashierId = signal<string | null>(null);

  // External tax override — set by POS based on fiscal responsibility
  taxesDisabled = signal(false);

  /**
   * Whether the Business charges IVA, for the rounding of a list price — it is
   * applied to the figure the customer pays. Pushed in by the POS from
   * `TaxRegimeService.isResponsable()`, the single authority, rather than
   * injected here: reaching for it from the cart would drag the DIAN config
   * graph into every screen that holds a line. Same shape as `taxesDisabled`.
   * Defaults to the same regime the authority defaults to (`no-responsable`).
   */
  businessChargesIva = signal(false);

  /**
   * Every Price List of the Business, pushed in by the POS from
   * `PriceListService.lists()`. The cart stores a cashier's pick as a bare id
   * (it has to survive localStorage), so this is how it turns that id back into
   * the rule when the line is repriced. Pushed rather than injected for the
   * same reason as `businessChargesIva`: injecting the service would drag its
   * Firestore graph into every spec that touches a cart.
   */
  priceLists = signal<PriceList[]>([]);

  /**
   * The Price List the cart is pricing through — the Client's, or the one the
   * cashier picked for the sale. Held in the service rather than on each line
   * because a quantity change has to re-run the very same rule the line was
   * priced with.
   */
  private priceListSignal = signal<PriceList | null>(null);

  // Signals for reactive state
  private cartSignal = signal<Cart>(this.getInitialCart());

  // Computed signals
  public cart = computed(() => this.cartSignal());
  public itemCount = computed(() => {
    const sum = this.cartSignal().items.reduce((acc, item) => acc + Number(item.quantity), 0);
    // Round to 2 decimal places to avoid floating point precision issues
    return Math.round(sum * 100) / 100;
  });
  public hasItems = computed(() => this.cartSignal().items.length > 0);
  /** The list every automatic line in this cart is being charged through. */
  public activePriceList = computed(() => this.priceListSignal());
  public cartTotal = computed(() => this.cartSignal().total);

  // Held orders — the shared, location-scoped pool lives in HeldOrderService (ADR-0007);
  // CartService re-exposes its live signals so existing consumers keep the same surface.
  public heldCarts = this.heldOrderService.heldOrders;
  public heldCartsCount = this.heldOrderService.heldOrdersCount;
  public canHoldMore = this.heldOrderService.canHoldMore;

  // Return/Exchange state signals
  private originalSaleSignal = signal<Sale | null>(null);
  private returnReasonSignal = signal<string>('');
  private returnModeSignal = signal<boolean>(false);

  // Return/Exchange computed signals
  public originalSale = computed(() => this.originalSaleSignal());
  public returnReason = computed(() => this.returnReasonSignal());
  public returnMode = computed(() => this.returnModeSignal());
  public hasReturnItems = computed(() => this.cartSignal().items.some(item => item.quantity < 0));
  public hasPositiveItems = computed(() => this.cartSignal().items.some(item => item.quantity > 0));
  public transactionType = computed<TransactionType>(() => {
    const hasReturns = this.hasReturnItems();
    const hasPositive = this.hasPositiveItems();
    if (hasReturns && hasPositive) return 'exchange';
    if (hasReturns) return 'return';
    return 'sale';
  });

  constructor() {
    // Auto-save cart to localStorage when it changes
    effect(() => {
      const cart = this.cartSignal();
      const cashierId = this.currentCashierId();
      if (cashierId) {
        // Always save, even if empty (to persist the cleared state)
        this.saveCartForCashier(cashierId, cart);
      }
    });
  }

  /**
   * Recalculate cart totals (e.g., when tax state changes)
   */
  recalculateTotals(): void {
    const cart = this.cartSignal();
    if (cart.items.length > 0) {
      this.cartSignal.set(this.calculateTotalsImmutable({ ...cart }));
    }
  }

  /**
   * Get initial cart (called before service is fully initialized)
   */
  private getInitialCart(): Cart {
    return {
      items: [],
      subtotal: 0,
      discount: 0,
      tax: 0,
      total: 0,
      employeeId: 'system',
      employeeName: 'Sistema',
    };
  }

  /**
   * Get empty cart with current employee info
   */
  private getEmptyCart(): Cart {
    const employeeInfo = this.employeeService.getCurrentEmployeeInfo();
    return {
      items: [],
      subtotal: 0,
      discount: 0,
      tax: 0,
      total: 0,
      employeeId: employeeInfo.employeeId,
      employeeName: employeeInfo.employeeName,
    };
  }

  /**
   * Initialize cart for a specific cashier
   * This should be called when opening POS with a cashier
   */
  initializeForCashier(cashierId: string): void {
    this.currentCashierId.set(cashierId);
    this.loadCartForCashier(cashierId);
  }

  /**
   * Clear current cashier context
   */
  clearCashierContext(): void {
    const cashierId = this.currentCashierId();
    if (cashierId) {
      // Save current empty state before clearing
      this.saveCartForCashier(cashierId, this.cartSignal());
    }
    this.currentCashierId.set(null);
    this.cartSignal.set(this.getEmptyCart());
  }

  private getCartKey(cashierId: string): string {
    return `pos-cart-${cashierId}`;
  }

  private loadCartForCashier(cashierId: string): void {
    const cartKey = this.getCartKey(cashierId);
    const savedCart = localStorage.getItem(cartKey);

    if (savedCart) {
      try {
        const cart = JSON.parse(savedCart);

        // Validate and fix item totals for all items
        const fixedItems = cart.items.map((item: CartItem) => {
          // Ensure price is set (fallback to basePrice if needed)
          const itemPrice = resolvePrice({
            basePrice: Number((item as any).basePrice),
            typedPrice: Number(item.price),
          }).price;
          // Ensure quantity is a number (fix string concatenation bug from localStorage)
          const quantity = Number(item.quantity) || 0;
          // Recalculate itemTotal to ensure it's correct
          const fixedItemTotal = itemPrice * quantity;

          return {
            ...item,
            price: itemPrice,
            quantity: quantity,
            itemTotal: fixedItemTotal,
          };
        });

        // Recalculate totals with fixed items
        const fixedCart = this.calculateTotalsImmutable({
          ...cart,
          items: fixedItems,
        });

        // Set the fixed cart state
        this.cartSignal.set(fixedCart);
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem(cartKey);
        this.cartSignal.set(this.getEmptyCart());
      }
    } else {
      // No saved cart, start fresh
      this.cartSignal.set(this.getEmptyCart());
    }
  }

  private saveCartForCashier(cashierId: string, cart: Cart): void {
    const cartKey = this.getCartKey(cashierId);
    // Always save the current state
    localStorage.setItem(cartKey, JSON.stringify(cart));
  }

  addToCart(
    product: Product,
    quantity: number = 1,
    options?: {
      variantId?: string;
      variantName?: string;
      variantAttributes?: { [key: string]: string };
      variantPrice?: number; // Optional variant price override
      variantCost?: number; // Optional variant cost override for profit calculation
      stockAtLocation?: number; // Stock at the specific location
      locationId?: string;
      locationName?: string;
    }
  ): void {
    if (!product.id) {
      return;
    }

    // Ensure quantity is always a number
    const numericQuantity = Number(quantity) || 1;

    this.cartSignal.update(currentCart => {
      // For products with variants, match by both product ID and variant ID
      // For simple products, match by product ID only
      const existingItemIndex = currentCart.items.findIndex(item => {
        const sameProduct = item.id === product.id;
        const sameVariant = options?.variantId
          ? item.variantId === options.variantId
          : !item.variantId;
        // Don't merge return items with regular items (different sign)
        const isExistingPositive = item.quantity >= 0;
        const isIncomingPositive = numericQuantity >= 0;
        const sameSign = isExistingPositive === isIncomingPositive;
        return sameProduct && sameVariant && sameSign;
      });

      let updatedItems: CartItem[];

      if (existingItemIndex !== -1) {
        // Update existing item - create new array with updated item. Adding onto
        // a line is a quantity change like any other, so it re-runs the list's
        // minimum: 3 + 7 units reach a threshold of 10 and drop to the rule.
        const existingItem = currentCart.items[existingItemIndex];
        const itemPrice = Number(existingItem.price) || 0;
        const existingQuantity = Number(existingItem.quantity) || 0;
        const newQuantity = existingQuantity + numericQuantity;
        updatedItems = currentCart.items.map((item, index) => {
          if (index !== existingItemIndex) return item;
          return priceIsSettled(item)
            ? { ...item, quantity: newQuantity, itemTotal: newQuantity * itemPrice }
            : this.priceLine(item, newQuantity);
        });
      } else {
        // Add new item - create new array with new item
        // The line freezes its catalogue price here. `product.price` is the old
        // V1 model's field, still ahead of the V2 `basePrice` as it always was.
        const catalogPrice = resolvePrice({
          basePrice: Number((product as any).price) || Number((product as any).basePrice),
          variantPrice: Number(options?.variantPrice),
        }).price;

        // Use provided stockAtLocation or fall back to product's stockAtLocation
        const stockAtLocation =
          options?.stockAtLocation !== undefined
            ? Number(options.stockAtLocation)
            : Number((product as any).stockAtLocation) || 0;

        // Get cost from product - check both baseCost and cost for backward compatibility
        const productCost = Number((product as any).baseCost) ?? Number((product as any).cost) ?? 0;

        const cartItem: CartItem = {
          ...product, // Spread all Product V2 fields
          id: product.id,
          catalogPrice,
          price: catalogPrice, // Ensure price field is set for backward compatibility
          baseCost: productCost, // Explicitly set baseCost for profit calculation
          quantity: numericQuantity,
          itemTotal: catalogPrice * numericQuantity,
          // Add variant and location information if provided
          variantId: options?.variantId,
          variantName: options?.variantName,
          variantAttributes: options?.variantAttributes,
          locationId: options?.locationId,
          locationName: options?.locationName,
          stockAtLocation: stockAtLocation, // Include stock at location
          discountAmount: 0, // Initialize discount amount
          // Include variant cost for profit calculation (overrides baseCost)
          ...(options?.variantCost !== undefined && { variantCost: Number(options.variantCost) }),
        };
        // A return line takes the price the customer actually paid; everything
        // else enters through the list, which has the last word on the figure,
        // dates included: validity is read here, once, and never re-read.
        const seeded = this.applySoldPrice(cartItem);
        updatedItems = [
          ...currentCart.items,
          priceIsSettled(seeded) ? this.withTotal(seeded) : this.priceLine(seeded, numericQuantity),
        ];
      }

      // Return new cart object with updated items
      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });

    // Play sound after adding to cart
    this.soundService.playAddToCart();
  }

  updateQuantity(productId: string, quantity: number, variantId?: string): void {
    // Ensure quantity is always a number
    const numericQuantity = Number(quantity) || 0;

    // Allow zero to remove, but allow negative quantities for returns
    if (numericQuantity === 0) {
      this.removeFromCart(productId, variantId);
      return;
    }

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        if (!matchesProduct || !matchesVariant) return item;

        // Clear any existing item-level discount when quantity changes
        // (the original discount was tied to a specific quantity)
        if (priceIsSettled(item)) {
          const itemPrice = resolvePrice({
            basePrice: Number(item.basePrice),
            typedPrice: Number(item.price),
          }).price;
          const rawTotal = numericQuantity * itemPrice;
          return {
            ...item,
            quantity: numericQuantity,
            discountAmount: 0,
            // Allow negative itemTotal for returns (negative quantities)
            itemTotal: numericQuantity < 0 ? rawTotal : Math.max(0, rawTotal),
          };
        }

        // The line's own quantity is what the list minimum counts, so crossing
        // it in either direction reprices this line and only this line.
        return { ...this.priceLine(item, numericQuantity), discountAmount: 0 };
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });
  }

  updateItemPrice(productId: string, newPrice: number, variantId?: string): void {
    const numericPrice = Number(newPrice) || 0;
    if (numericPrice <= 0) return;

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        const itemQuantity = Number(item.quantity) || 0;

        if (matchesProduct && matchesVariant) {
          return {
            ...item,
            price: numericPrice,
            priceManuallySet: true,
            priceListId: undefined,
            priceListName: undefined,
            discountAmount: 0,
            itemTotal: itemQuantity * numericPrice,
          };
        }
        return item;
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });
  }

  /**
   * Update a cart line's displayed name. Used by the POS in-cart inline
   * editor when the "Usar Nombre POS" setting is on. The override is
   * snapshotted onto the cart item and flows through to the receipt and
   * the electronic-invoice line description. A blank/whitespace-only value
   * is ignored to avoid sending an empty line description downstream.
   */
  updateItemName(productId: string, newName: string, variantId?: string): void {
    const trimmed = (newName ?? '').trim();
    if (!trimmed) return;

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        if (matchesProduct && matchesVariant) {
          return { ...item, name: trimmed };
        }
        return item;
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });
  }

  /**
   * Store the per-sale dynamic service cost typed in the POS on a cart line
   * (AP-2). Cost is not price, so this leaves the line's price and totals
   * untouched — it only records what the business will owe the third party,
   * which flows to `SaleItem.cost` at checkout for gated lines.
   */
  updateServiceCost(productId: string, cost: number, variantId?: string): void {
    const numericCost = Number(cost) || 0;

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        if (matchesProduct && matchesVariant) {
          return { ...item, dynamicCost: numericCost };
        }
        return item;
      });

      return { ...currentCart, items: updatedItems };
    });
  }

  updateItemTaxRate(productId: string, taxRate: number, variantId?: string): void {
    const numericRate = Number(taxRate);
    if (!Number.isFinite(numericRate) || numericRate < 0) return;

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;

        if (matchesProduct && matchesVariant) {
          return { ...item, taxRate: numericRate };
        }
        return item;
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });
  }

  removeFromCart(productId: string, variantId?: string): void {
    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.filter(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        // Keep items that DON'T match both product and variant
        return !(matchesProduct && matchesVariant);
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
        // Emptying the cart by removing its last item also unlinks the order name, so a
        // product-less cart never keeps a stale name badge (ADR-0007 slice #5).
        label: updatedItems.length === 0 ? undefined : currentCart.label,
      });
    });
  }

  applyDiscount(amount: number, type: 'fixed' | 'percentage' = 'fixed'): void {
    this.cartSignal.update(currentCart => {
      let discount: number;

      if (type === 'percentage') {
        // Calculate percentage discount (no rounding - only final total is rounded for cash)
        discount = (currentCart.subtotal * amount) / 100;
      } else {
        // For fixed discounts (no rounding - only final total is rounded for cash)
        discount = Math.min(amount, currentCart.subtotal);
      }

      return this.calculateTotalsImmutable({
        ...currentCart,
        discount,
        discountValue: amount,
        discountType: type,
      });
    });
  }

  applyItemDiscount(productId: string, discount: number, variantId?: string): void {
    const numericDiscount = Number(discount) || 0;

    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;

        if (matchesProduct && matchesVariant) {
          // No rounding on item discount - only final total is rounded for cash
          const itemQuantity = Number(item.quantity) || 0;
          const itemPrice = resolvePrice({
            basePrice: Number(item.basePrice),
            typedPrice: Number(item.price),
          }).price;
          const rawItemTotal = itemQuantity * itemPrice - numericDiscount;
          // Allow negative itemTotal for returns (negative quantities)
          const itemTotal = itemQuantity < 0 ? rawItemTotal : Math.max(0, rawItemTotal);

          return {
            ...item,
            discountAmount: numericDiscount,
            itemTotal,
          };
        }

        return item;
      });

      return this.calculateTotalsImmutable({
        ...currentCart,
        items: updatedItems,
      });
    });
  }

  /**
   * Put the cart on a Price List and reprice what nobody decided by hand.
   *
   * Called when the Client changes: silently charging the pre-client price is
   * the failure this exists to prevent, so the caller gets back the counts it
   * needs to say what happened ("2 líneas cambiaron, 1 se respetó").
   *
   * @param list - The Client's list, already loaded, or `null` for catalogue.
   * @returns How many lines moved, and how many were left as a person set them.
   */
  applyPriceList(list: PriceList | null): RepricingSummary {
    // A Client arriving is what discards the cashier's sale-level pick — see
    // CONTEXT.md, "Active price list". Read before the pass, so the aviso can
    // still name it.
    const discarded = listById(this.priceLists(), this.cartSignal().priceListPinned)?.name;
    return { ...this.reprice(list, undefined), ...(discarded && { discarded }) };
  }

  /**
   * The list the **cashier** picked for this whole sale — how a taller with no
   * Client record is charged wholesale in one move.
   *
   * Same repricing as a Client's list, and it is recorded as a pick so the next
   * Client can discard it. `null` is a deliberate Precio de Venta, not "nobody
   * picked": it survives on the cart exactly like a list would.
   */
  setSalePriceList(list: PriceList | null): RepricingSummary {
    return this.reprice(list, list?.id ?? null);
  }

  /**
   * Put the cart on `list` and reprice every line nobody decided by hand.
   *
   * @param pinned - What to record as the cashier's sale-level pick:
   *   `undefined` clears it (a Client arrived), an id or `null` sets it.
   */
  private reprice(list: PriceList | null, pinned: string | null | undefined): RepricingSummary {
    this.priceListSignal.set(list);

    let counts: RepricingCounts = { repriced: 0, respected: 0, atCatalogue: 0 };

    this.cartSignal.update(currentCart => {
      const { lines, ...tally } = repriceLines(currentCart.items, this.pricingContext(list));
      counts = tally;

      return this.calculateTotalsImmutable({
        ...currentCart,
        // A line the engine left alone comes back by reference, and its
        // `itemTotal` is not ours to touch: on a discounted line it already has
        // the discount subtracted (`applyItemDiscount`), so recomputing it here
        // would silently drop the discount off every respected line.
        items: lines.map((item, index) =>
          item === currentCart.items[index] ? item : this.withTotal(item)
        ),
        priceListId: list?.id,
        priceListPinned: pinned,
      });
    });

    return counts;
  }

  /**
   * Pin one line to a Price List of its own — the exception that must not move
   * the rest of the cart.
   *
   * The pin is stored apart from the list that ends up charging the line: a list
   * pinned on a product it does not cover charges catalogue and stamps nothing,
   * and the pin still has to survive the next Client.
   *
   * @param list - The list to pin, or `null` for a deliberate Precio de Venta.
   */
  setLinePriceList(productId: string, list: PriceList | null, variantId?: string): void {
    this.pinLine(productId, variantId, list?.id ?? null);
  }

  /** Take the pin off one line: it follows the sale's list again. */
  clearLinePriceList(productId: string, variantId?: string): void {
    this.pinLine(productId, variantId, undefined);
  }

  private pinLine(
    productId: string,
    variantId: string | undefined,
    pinned: string | null | undefined
  ): void {
    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        if (!this.isLine(item, productId, variantId)) return item;
        return this.priceLine({ ...item, priceListPinned: pinned }, Number(item.quantity) || 0);
      });

      return this.calculateTotalsImmutable({ ...currentCart, items: updatedItems });
    });
  }

  /** One line of the cart — a product, or one variant of it. */
  private isLine(item: CartItem, productId: string, variantId?: string): boolean {
    if (item.id !== productId) return false;
    return variantId ? item.variantId === variantId : !item.variantId;
  }

  /**
   * Drop the price a cashier typed on one line and put it back on the cart's list.
   *
   * The counterpart to `updateItemPrice`: a typed price is a decision, and this
   * is how the person who made it takes it back. Without it the only way out is
   * deleting the line and adding it again, which nobody guesses.
   */
  releaseManualPrice(productId: string, variantId?: string): void {
    this.cartSignal.update(currentCart => {
      const updatedItems = currentCart.items.map(item => {
        const matchesProduct = item.id === productId;
        const matchesVariant = variantId ? item.variantId === variantId : !item.variantId;
        if (!matchesProduct || !matchesVariant) return item;

        const released = { ...item, priceManuallySet: false };
        return this.priceLine(released, Number(item.quantity) || 0);
      });

      return this.calculateTotalsImmutable({ ...currentCart, items: updatedItems });
    });
  }

  /**
   * Put the cart back on a list **without touching a single price**.
   *
   * A cart restored from localStorage and a held order resumed hours later both
   * come back with their prices already decided — a held order keeps the
   * figures it was paused with, banner and all. The list is restored anyway
   * because the next quantity change on a line has to re-run its minimum.
   */
  restorePriceList(list: PriceList | null): void {
    this.priceListSignal.set(list);
  }

  /**
   * Refund a returned line at the price the customer paid, when the return is
   * linked to its original sale (PL-12, AC 4).
   *
   * The minimum-quantity guard already keeps every list off a negative line, so
   * a return was never charged *today's* rule — but catalogue is not the same
   * fact as "what was paid". A sale closed through a wholesale list at $5.250
   * was refunded at the $6.000 counter price, systematically in the customer's
   * favour, and Price Lists are what made the two figures diverge.
   *
   * Frozen rather than merely priced: the figure is a settled fact from a closed
   * document, exactly like a quotation's, so no later quantity change or list
   * pick may renegotiate it (see {@link priceIsSettled}).
   *
   * The figure taken is the one the customer *paid*, per unit — the line's own
   * discount folded in by {@link soldUnitPrice}, not `sold.price`, which is what
   * the line cost before that discount. A line sold at $5.250 with $500 off two
   * units is refunded at $5.000 a unit, and a partial return gets its share of
   * the discount because the division prorates it.
   *
   * Silent when the return carries no original sale — returns without a receipt
   * stay possible and keep falling back to the counter price.
   */
  private applySoldPrice(item: CartItem): CartItem {
    const sale = this.originalSaleSignal();
    if (!this.returnModeSignal() || !sale) return item;

    const sold = (sale.items ?? []).find(
      line =>
        line.productId === item.id &&
        (line.variantId || undefined) === (item.variantId || undefined)
    );
    if (!sold) return item;

    return {
      ...item,
      price: soldUnitPrice(sold),
      // The list that charged it, carried through so the credit note and the
      // sale detail name the same one the original does.
      priceListId: sold.priceListId,
      priceListName: sold.priceListName,
      priceFrozen: true,
    };
  }

  /**
   * Re-run the price of one line through the cart's list.
   *
   * Every guard is re-evaluated, the minimum quantity included — that is what
   * lets a line bumped from 3 to 10 fall to the wholesale price without being
   * deleted and added again.
   */
  private priceLine(item: CartItem, quantity: number, list = this.priceListSignal()): CartItem {
    return this.withTotal(priceLine(item, quantity, this.pricingContext(list)));
  }

  /**
   * The engine's inputs that live on this service: every list of the Business
   * (to resolve a pin travelling as a bare id), the list the cart is priced
   * through, and whether the Business charges IVA.
   */
  private pricingContext(list = this.priceListSignal()): LinePricingContext {
    return {
      lists: this.priceLists(),
      activeList: list,
      businessChargesIva: this.businessChargesIva(),
    };
  }

  /**
   * The cart's name for what a priced line adds to the sale. The engine leaves
   * it to the caller because an invoice calls the same figure `total`.
   */
  private withTotal(item: CartItem): CartItem {
    return { ...item, itemTotal: lineTotal(Number(item.price) || 0, Number(item.quantity) || 0) };
  }

  setClient(clientId: string): void {
    this.cartSignal.update(currentCart => ({
      ...currentCart,
      clientId,
    }));
  }

  setEmployee(employeeId: string): void {
    this.cartSignal.update(currentCart => ({
      ...currentCart,
      employeeId,
    }));
  }

  /**
   * Rehydrate the cart from an existing quotation so it can be edited in the POS.
   * Rebuilds editable lines from the frozen `SaleItem[]`, restores the assigned
   * client, and restores the order-level discount as a fixed amount (the quote
   * only persists the computed discount value, not the original percentage, so
   * the customer-facing amount stays frozen). Totals are recomputed from the
   * rehydrated lines.
   *
   * @param pricesStillPromised - `false` for a quotation past its own validity,
   *   whose figures are no longer owed to anybody. The lines then arrive
   *   unfrozen and the caller must follow with {@link repriceLapsedQuote},
   *   which is where the catalogue figures this rehydration cannot recover come
   *   back from.
   */
  loadFromSale(sale: Sale, pricesStillPromised = true): void {
    const items = (sale.items ?? []).map(item => saleItemToCartItem(item, pricesStillPromised));
    const cart: Cart = {
      items,
      subtotal: 0,
      discount: sale.discount ?? 0,
      discountValue: sale.discount ?? 0,
      discountType: 'fixed',
      tax: 0,
      total: 0,
      clientId: sale.clientId,
      // The list the quotation was priced through, so the sale it becomes still
      // names it. Its lines arrive frozen, so nothing is repriced by restoring it.
      priceListId: sale.priceListId,
      employeeId: sale.employeeId,
      employeeName: sale.employeeName,
    };
    this.cartSignal.set(this.calculateTotalsImmutable(cart));
  }

  /**
   * Put a lapsed quotation's lines back through today's rules (PL-12, AC 7).
   *
   * A Quote is a price promise, and while it stands no Price List moves its
   * figures — not even one that expired in the meantime. Its own validity is the
   * clock that governs it (CONTEXT.md, "Active price list"), and once that runs
   * out the promise is simply over: the lines are priced like any other, through
   * whatever list reaches them today, and the cashier is told.
   *
   * Two steps, and the first is the one that is easy to miss: the lines are
   * re-seeded from the catalogue **before** the engine runs. A rehydrated quote
   * line carries the charged price in `basePrice`, so repricing it as it arrives
   * would take today's wholesale percentage off a figure that had last year's
   * taken off it already — a discount compounded once per lapsed quote,
   * invisible because the result still looks like a plausible price.
   *
   * A line whose product is gone from the catalogue keeps its quoted figure and
   * stays frozen: with nothing to price it from, inventing a number is worse
   * than honouring a lapsed one.
   *
   * @returns What the repricing did, for the caller to report.
   */
  repriceLapsedQuote(catalog: CatalogSnapshot): RepricingCounts {
    const cart = this.cartSignal();
    const reseeded = cart.items.map(item => this.reseedFromCatalog(item, catalog));
    const outcome = repriceLines(reseeded, this.pricingContext());

    this.cartSignal.set(
      this.calculateTotalsImmutable({
        ...cart,
        items: outcome.lines.map(line => this.withTotal(line)),
      })
    );

    return {
      repriced: outcome.repriced,
      respected: outcome.respected,
      atCatalogue: outcome.atCatalogue,
    };
  }

  /**
   * Give one rehydrated quote line its catalogue figures back, so the engine has
   * something honest to price from. See {@link repriceLapsedQuote}.
   */
  private reseedFromCatalog(item: CartItem, catalog: CatalogSnapshot): CartItem {
    const product = catalog.products.get(item.id ?? '');
    // Nothing to price from: leave the quoted figure alone rather than invent one.
    if (!product) return { ...item, priceFrozen: true };

    const variantPrice = item.variantId
      ? catalog.variantPrices.get(`${item.id}:${item.variantId}`)
      : undefined;
    const catalogPrice = resolvePrice({
      basePrice: Number(product.basePrice),
      variantPrice,
    }).price;

    return {
      ...item,
      basePrice: product.basePrice,
      categoryId: product.categoryId,
      taxRate: product.taxRate,
      // PL-7 exceptions live here, and a line that arrived without them would
      // silently miss the price a person negotiated for this product.
      listPrices: product.listPrices,
      catalogPrice,
      price: catalogPrice,
      // The quotation's stamps name the list that charged it *then*; today's
      // pricing writes its own, and a stale one left behind would put a chip on
      // the line naming a list that did not charge it.
      priceListId: undefined,
      priceListName: undefined,
      priceListBlockedBy: undefined,
    };
  }

  /**
   * Re-stamp the cart lines' stock snapshots from a freshly-read view of the location's stock.
   *
   * Called at the two moments a cart re-enters the screen older than it left: mounting a
   * register, whose working cart is restored from localStorage, and resuming a held order.
   * Lines it re-stamps are marked `stockMeasured`, which is what lets a fresh 0 be believed
   * downstream instead of being read as the add-to-cart default. Advisory only: it never
   * changes quantities or removes lines — the cashier decides, and the `createSale` transaction
   * stays the authoritative gate. Totals are untouched by design (stock does not affect them).
   */
  applyFreshStock(products: CurrentStock[]): void {
    const cart = this.cartSignal();
    const items = refreshCartItemStock(cart.items, products);
    if (items !== cart.items) {
      this.cartSignal.set({ ...cart, items });
    }
  }

  clearCart(): void {
    const cashierId = this.currentCashierId();

    // Create empty cart with current employee
    const emptyCart = this.getEmptyCart();

    // Update signal (this will trigger effect to save to localStorage)
    this.cartSignal.set(emptyCart);

    // Explicitly save empty cart to localStorage for this cashier
    if (cashierId) {
      this.saveCartForCashier(cashierId, emptyCart);
    }
  }

  // Immutable version of calculateTotals that returns new cart
  // Stores both raw (unrounded) and rounded totals per Colombian Resolución 1092 de 2020
  // Rounding only applies to cash payments due to coin limitations
  private calculateTotalsImmutable(cart: Cart): Cart {
    // Rounding always to $50 per Colombian Resolución 1092 de 2020
    const roundingRule = 'to_50' as const;

    // Calculate subtotal (no rounding on subtotal - only on final total for cash)
    // Ensure all itemTotal values are numbers
    const subtotal = cart.items.reduce((sum, item) => sum + Number(item.itemTotal), 0);

    // Recalculate discount if it's a percentage discount
    let discount = cart.discount || 0;
    if (cart.discountType === 'percentage' && cart.discountValue) {
      // Recalculate the discount based on the subtotal
      discount = (subtotal * cart.discountValue) / 100;
    }

    // Check if taxes are disabled (from fiscal responsibility / per-sale toggle)
    const disableTaxes = this.taxesDisabled();

    // Calculate tax per-item using each product's tax rate
    // Colombian IVA: 0.19 (19% general), 0.05 (5% differential), 0 (exempt/excluded)
    let tax = 0;
    if (!disableTaxes) {
      cart.items.forEach(item => {
        const itemPrice = resolvePrice({
          basePrice: Number(item.basePrice),
          typedPrice: Number(item.price),
        }).price;
        const itemQuantity = Number(item.quantity) || 0;
        const itemSubtotal = itemPrice * itemQuantity;
        const itemDiscount = Number(item.discountAmount) || 0;

        // Calculate proportional discount if there's an order-level discount
        let proportionalDiscount = 0;
        if (discount > 0 && subtotal > 0) {
          const proportion = itemSubtotal / subtotal;
          proportionalDiscount = discount * proportion;
        }

        // Allow negative taxable amounts for returns (will result in tax refund)
        const taxableAmount = itemSubtotal - itemDiscount - proportionalDiscount;
        // Use product's taxRate if available, otherwise default to 0.19 (19% IVA)
        // For exempt products, taxRate should be 0
        const productTaxRate = (item as any).taxRate ?? 0.19;
        tax += taxableAmount * productTaxRate;
      });
    }

    // Calculate total item discounts (sum of all item-level discounts for Firestore aggregation)
    const totalItemDiscount = cart.items.reduce(
      (sum, item) => sum + (Number(item.discountAmount) || 0),
      0
    );

    // Calculate raw total (unrounded - for electronic payments)
    // Round to nearest peso first to avoid fractional centavos from per-item IVA calculations
    // (COP has no decimal places — fractional values cause incorrect $50 rounding)
    const rawTotal = Math.round(subtotal - discount + tax);

    // Calculate rounded total (for cash payments only per Colombian Resolución 1092 de 2020)
    const total = applyRounding(rawTotal, roundingRule);

    // Return new cart object with both raw and rounded totals
    return {
      ...cart,
      subtotal,
      discount,
      totalItemDiscount, // Sum of item-level discounts (for Firestore aggregation)
      tax,
      total, // Rounded - use for cash payments
      rawTotal, // Unrounded - use for electronic payments (card, transfer, Nequi, etc.)
    };
  }

  quickAddByBarcode(product: Product): void {
    this.addToCart(product, 1);
  }

  getCart(): Cart {
    return this.cartSignal();
  }

  // ==================== RETURNS & EXCHANGES ====================

  /**
   * Set the original sale for a return/exchange (optional linkage)
   */
  setOriginalSale(sale: Sale | null): void {
    this.originalSaleSignal.set(sale);
  }

  /**
   * Set the return reason (optional)
   */
  setReturnReason(reason: string): void {
    this.returnReasonSignal.set(reason);
  }

  /**
   * Set return mode (enables adding out-of-stock products as returns)
   */
  setReturnMode(enabled: boolean): void {
    this.returnModeSignal.set(enabled);
  }

  /**
   * Toggle return mode
   */
  toggleReturnMode(): void {
    this.returnModeSignal.update(v => !v);
  }

  /**
   * Add a product as a return item (negative quantity)
   */
  addReturnItem(
    product: Product,
    quantity: number,
    options?: {
      variantId?: string;
      variantName?: string;
      variantAttributes?: { [key: string]: string };
      variantPrice?: number;
      variantCost?: number;
      locationId?: string;
      locationName?: string;
    }
  ): void {
    // Ensure quantity is negative for returns
    const returnQuantity = -Math.abs(Number(quantity) || 1);
    this.addToCart(product, returnQuantity, options);
  }

  /**
   * Clear return state (original sale, reason, and return mode)
   */
  clearReturnState(): void {
    this.originalSaleSignal.set(null);
    this.returnReasonSignal.set('');
    this.returnModeSignal.set(false);
  }

  /**
   * Clear cart and return state together
   */
  clearCartAndReturnState(): void {
    this.clearCart();
    this.clearReturnState();
  }

  // ==================== HELD ORDERS ====================
  //
  // Held orders are a location-scoped Business collection shared in real time across every
  // terminal at the Location (ADR-0007). CartService owns the current cart; HeldOrderService
  // owns persistence and the live pool. These methods orchestrate the two.

  /**
   * Hold (pause) the current cart into the shared, location-scoped pool.
   * @param clientName Optional client name for display
   * @param notes Optional notes for the held order
   * @param label Optional user-provided name to identify the paused session
   * @returns true if order was held, false if cart is empty, the soft cap is reached, or no
   *   Location is selected
   */
  async holdCart(clientName?: string, notes?: string, label?: string): Promise<boolean> {
    const currentCart = this.cartSignal();

    // Don't hold empty carts
    if (currentCart.items.length === 0) {
      return false;
    }

    // Check if limit reached (cheap guard before the async write)
    if (!this.canHoldMore()) {
      return false;
    }

    const employeeInfo = this.employeeService.getCurrentEmployeeInfo();

    const held = await this.heldOrderService.hold({
      cart: { ...currentCart },
      heldAt: new Date(),
      heldByName: employeeInfo.employeeName,
      label: label || undefined,
      clientName: clientName || undefined,
      notes: notes || undefined,
      itemCount:
        Math.round(currentCart.items.reduce((sum, item) => sum + Number(item.quantity), 0) * 100) /
        100,
      total: currentCart.total,
    });

    // Only clear the current cart once the paused order is persisted
    if (held) {
      this.clearCart();
    }

    return held;
  }

  /**
   * Resume a held order by replacing the current cart.
   * @param orderId The ID of the held order to resume
   * @returns true if order was resumed, false if it no longer exists (already taken)
   */
  async resumeCart(orderId: string): Promise<boolean> {
    const order = await this.heldOrderService.resume(orderId);
    if (!order) {
      return false;
    }

    // Restore the cart from the claimed order, inheriting the order's name so it stays
    // visible while editing and survives a later re-pause (ADR-0007 slice #5).
    this.cartSignal.set({ ...order.cart, label: order.label });
    return true;
  }

  /**
   * Set (or clear) the name of the order the current cart is working on.
   * Touches only the `label`; items and totals are untouched — this is how the badge's
   * ✕ "unlinks" the name without emptying the cart (ADR-0007 slice #5).
   * @param label The new order name, or `undefined`/empty to unlink it.
   */
  setOrderLabel(label: string | undefined): void {
    this.cartSignal.update(cart => ({ ...cart, label: label || undefined }));
  }

  /**
   * Delete a held order without resuming it.
   * @param orderId The ID of the held order to delete
   * @returns true if order was deleted, false if it no longer exists
   */
  deleteHeldCart(orderId: string): Promise<boolean> {
    return this.heldOrderService.delete(orderId);
  }
}
